class TabsContainer extends HTMLElement {
  #loadedScripts = new Set();
  #visibilityObserver;
  #clickHandlers = new Map();
  #selectedTab = null;

  constructor() {
    super();

    this.tabs = null;
    this.panel = null;
  }

  connectedCallback() {
    this.tabs = this.querySelectorAll('[data-ref="tab"]');
    this.panel = this.querySelector('[data-ref="tabs-panel"]');

    this.tabs.forEach((tab) => {
      const handler = () => {
        this.#visibilityObserver?.disconnect();
        this.#resetTabs();
        this.#selectTab(tab);
      };
      this.#clickHandlers.set(tab, handler);
      tab.addEventListener('click', handler);
    });

    if (!this.panel || !this.tabs.length || this.#selectedTab) return;
    if (this.hasAttribute('data-defer-tabs') && !window.Shopify?.designMode && 'IntersectionObserver' in window) {
      this.#visibilityObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#visibilityObserver.disconnect();
        this.#selectTab(this.tabs[0]);
      }, { rootMargin: '400px 0px', threshold: 0 });
      this.#visibilityObserver.observe(this);
      return;
    }
    this.#selectTab(this.tabs[0]);
  }

  disconnectedCallback() {
    this.#visibilityObserver?.disconnect();
    this.#clickHandlers.forEach((handler, tab) => tab.removeEventListener('click', handler));
    this.#clickHandlers.clear();
  }

  #selectTab(tab) {
    if (!tab || !this.panel) return;
    const tab_content = tab.querySelector('template').content.querySelector('[data-ref="tab-content"]');

    this.panel.innerHTML = tab_content.innerHTML;
    this.#activateScripts(this.panel);
    tab.classList.add('active');
    this.#selectedTab = tab;
    this.setAttribute('data-tabs-ready', 'true');
  }

  #activateScripts(container) {
    for (const script of [...container.querySelectorAll('script')]) {
      const src = script.getAttribute('src');

      if (src) {
        if (this.#loadedScripts.has(src)) {
          script.remove();
          continue;
        }
        this.#loadedScripts.add(src);
      }

      const activeScript = document.createElement('script');

      for (const attr of script.attributes) {
        activeScript.setAttribute(attr.name, attr.value);
      }

      activeScript.textContent = script.textContent;
      script.replaceWith(activeScript);
    }
  }

  #resetTabs() {
    this.tabs.forEach((tab) => {
      tab.classList.remove('active');
    });
    this.panel.innerHTML = '';
  }
}

if (!customElements.get('tabs-container')) {
  customElements.define('tabs-container', TabsContainer);
}
